import { useAtom, useSetAtom } from 'jotai';
import Axios, { AxiosResponse } from 'axios';
import { environment } from '../../environment';
import { errorCatcher, snackbarSettingsAtom } from '../../hooks';
import { useLogin } from '../login';
import { IAnnouncement, announcementsAtom, announcementsLoadingAtom, activeAnnouncementAtom } from './types';

type ListAnnouncements = () => Promise<void>;
type CreateAnnouncement = (payload: Omit<IAnnouncement, 'id' | 'created_at' | 'updated_at' | 'status'>) => Promise<IAnnouncement | null>;
type UpdateAnnouncement = (id: number, payload: Partial<IAnnouncement>) => Promise<IAnnouncement | null>;
type DeleteAnnouncement = (id: number) => Promise<boolean>;

type AnnouncementKeyFunctions = {
    list: ListAnnouncements;
    create: CreateAnnouncement;
    update: UpdateAnnouncement;
    remove: DeleteAnnouncement;
}

type UseAnnouncementsService = [
    IAnnouncement[],
    boolean,
    AnnouncementKeyFunctions
];

// #region useAnnouncementsService
export const useAnnouncementsService = (): UseAnnouncementsService => {
    const [announcements, setAnnouncements] = useAtom(announcementsAtom);
    const [loading, setLoading] = useAtom(announcementsLoadingAtom);
    const setSnackbar = useSetAtom(snackbarSettingsAtom);
    const [, authToken, loginFunctions] = useLogin();

    const list: ListAnnouncements = async () => {
        setLoading(true);
        const response: AxiosResponse<IAnnouncement[]> | void = await Axios.get(`${environment.v2Url}/announcements`, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (response) {
            setAnnouncements(response.data || []);
        } else {
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to load announcements' });
        }
        setLoading(false);
    };

    const create: CreateAnnouncement = async (payload) => {
        const response: AxiosResponse<IAnnouncement> | void = await Axios.post(`${environment.v2Url}/announcements`, payload, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (response) {
            setSnackbar({ show: true, snackbarLevel: 'info', text: 'Announcement created' });
            await list();
            return response.data;
        } else {
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to create announcement' });
            return null;
        }
    };

    const update: UpdateAnnouncement = async (id, payload) => {
        const response: AxiosResponse<IAnnouncement> | void = await Axios.put(`${environment.v2Url}/announcements/${id}`, payload, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (response) {
            setSnackbar({ show: true, snackbarLevel: 'info', text: 'Announcement updated' });
            await list();
            return response.data;
        } else {
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to update announcement' });
            return null;
        }
    };

    const remove: DeleteAnnouncement = async (id) => {
        const response: AxiosResponse<{ success: boolean }> | void = await Axios.delete(`${environment.v2Url}/announcements/${id}`, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (response) {
            setSnackbar({ show: true, snackbarLevel: 'info', text: 'Announcement deleted' });
            await list();
            return true;
        } else {
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to delete announcement' });
            return false;
        }
    };

    return [announcements, loading, { list, create, update, remove }];
};

type DismissAnnouncement = () => Promise<void>;
type RefreshAnnouncement = () => Promise<void>;

type ActiveAnnouncementFunctions = {
    dismiss: DismissAnnouncement;
    refresh: RefreshAnnouncement;
}

type UseActiveAnnouncement = [
    IAnnouncement | null,
    ActiveAnnouncementFunctions
];

// #region useActiveAnnouncement
export const useActiveAnnouncement = (): UseActiveAnnouncement => {
    const [activeAnnouncement, setActiveAnnouncement] = useAtom(activeAnnouncementAtom);
    const setSnackbar = useSetAtom(snackbarSettingsAtom);
    const [, authToken, loginFunctions] = useLogin();

    const refresh: RefreshAnnouncement = async () => {
        const response: AxiosResponse<IAnnouncement | null> | void = await Axios.get(`${environment.v2Url}/announcements/active`, {
            headers: { 'X-Wallmates-Auth': authToken }
        }).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (response) {
            setActiveAnnouncement(response.data || null);
        }
    };

    const dismiss: DismissAnnouncement = async () => {
        if (!activeAnnouncement?.id) return;
        const response: AxiosResponse<{ success: boolean }> | void = await Axios.post(
            `${environment.v2Url}/announcements/${activeAnnouncement.id}/dismiss`,
            {},
            { headers: { 'X-Wallmates-Auth': authToken } }
        ).catch((err: any) => errorCatcher(err, loginFunctions.logout));
        if (response) {
            setActiveAnnouncement(null);
        } else {
            setSnackbar({ show: true, snackbarLevel: 'error', text: 'Failed to dismiss announcement' });
        }
    };

    return [activeAnnouncement, { dismiss, refresh }];
};
